<template>
  <div
    :class="{
      'card': useCardStyle,
      'padding': useCardStyle,
      'card--dark': isTorchysTheme,
      'reverse-colors': isTorchysTheme,
    }"
  >
    <div class="layout layout--horizontal">
      <div class="grow dist-y-half">
        <h2 class="title title--4">
          {{ summaryCardDetails.headerText }}
        </h2>

        <button
          v-if="isScanToCheckInEnabled"
          class="button button--primary button--small button--auto-width"
          type="button"
          @click="$openModal('loyaltyScanModal')"
        >
          {{ loyaltyScanModalCtaContent }}
        </button>

        <p
          v-if="summaryCardDetails.availableRewardsText"
          class="primary"
        >
          <b>{{ summaryCardDetails.availableRewardsText }}</b>
        </p>

        <p
          v-if="summaryCardDetails.descriptionText"
          class="smaller"
        >
          {{ summaryCardDetails.descriptionText }}
        </p>

        <button
          v-if="!loyalty.isEnrolled"
          class="button button--auto-width button--primary"
          type="button"
          @click="enrollInLoyalty"
        >
          {{ content.Loyalty.metadata.unenrolledButtonCta }}
        </button>

        <p
          v-else-if="displayRewardsActivityLink"
          class="title title--4"
        >
          <router-link
            :class="['link regular', isTorchysTheme ? 'dark' : 'primary']"
            :to="{ name: 'loyaltyActivity' }"
          >
            See Rewards Activities
          </router-link>
        </p>

        <p v-if="!hideLearnMore">
          <router-link
            class="link regular bigger dark"
            :to="{
              name: 'faqs',
              params: { merchantId: $route.params.merchantId }
            }"
          >
            Learn More
          </router-link>
        </p>
      </div>

      <div class="flex-column justify-between gap no-shrink">
        <img
          v-if="summaryCardDetails.hasImgLogo"
          class="image image--logo-smaller no-spacing"
          :alt="summaryCardDetails.imageAlt"
          :src="summaryCardDetails.imgSrc"
        >

        <loyalty-progress-ring v-else />

        <button
          v-if="isPointsGiftingEnabled"
          class="button button--rounded button--small regular button--primary no-spacing"
          type="button"
          @click="$openModal('giftingPointsModal')"
        >
          {{ pointsGifitingCta }}
        </button>
      </div>
    </div>
  </div>
</template>


<script>
  import events from '@/services/events';
  import { mapGetters } from 'vuex';

  export default {
    name: 'LoyaltySummaryCard',

    props: {
      hideLearnMore: {
        type: Boolean,
        default: false
      },

      useCardStyle: {
        type: Boolean,
        default: true
      }
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('offers', ['loyalty', 'rewardCount']),

      isPointsGiftingEnabled() {
        const hasPrimaryCurrency = this.loyalty.primaryLedger?.currency;

        if (hasPrimaryCurrency) {
          const { isActive = false, minPointsPerGift = 1 } = this.loyalty.primaryLedger.currency.pointGiftPolicy || { isActive: false, minPointsPerGift: 1 };

          return isActive && this.loyalty.currentBalance >= minPointsPerGift;
        }

        return false;
      },

      pointsGifitingCta() {
        return this.content.Loyalty.metadata.giftPointsButtonCta;
      },

      displayRewardsActivityLink() {
        const { memberActivityUIEnabled } = this.merchant.options.loyaltyOptions;

        return memberActivityUIEnabled && this.merchant.features['loyalty-activity-tab-enabled'];
      },

      isTorchysTheme() {
        return this.useCardStyle && this.merchant.name.indexOf('Torchy') === 0;
      },

      isScanToCheckInEnabled() {
        const { loyaltyCheckInEnabled, loyaltyCardCheckInEnabled, loyaltyReceiptScanningEnabled } = this.merchant.options.loyaltyOptions;

        return this.loyalty.isEnrolled && loyaltyCardCheckInEnabled && (loyaltyCheckInEnabled || loyaltyReceiptScanningEnabled);
      },

      loyaltyScanModalCtaContent() {
        const { loyaltyCheckInEnabled, loyaltyReceiptScanningEnabled } = this.merchant.options.loyaltyOptions;

        let scanCta = '';

        if (loyaltyCheckInEnabled) scanCta = this.content.ScanToCheckInLoyaltyScreen.metadata.scanToCheckInCta;

        if (loyaltyReceiptScanningEnabled) scanCta = this.content.ScanToEarnLoyaltyScreen.metadata.scanToEarnCta;

        if (loyaltyCheckInEnabled && loyaltyReceiptScanningEnabled) scanCta = this.content.ScanToCheckInAndEarnLoyaltyScreen.metadata.scanToEarnCta;

        return scanCta;
      },

      summaryCardDetails() {
        const obj = {
          headerText: this.content.Loyalty.metadata.unenrolledTitle,
          descriptionText: this.content.Loyalty.metadata.unenrolledSubtitle,
          hasImgLogo: !!this.content.LoyaltyEnrollment.metadata.imageUrl,
          imgSrc: this.content.LoyaltyEnrollment.metadata.imageUrl,
          imageAlt: this.merchant.name
        };

        if (this.loyalty.isEnrolled) {
          obj.headerText = `Loyalty ${this.loyalty.currency.pluralNameCapitalCase}`;
          obj.descriptionText = this.loyalty.pointsUntilNextReward
            ? `${this.loyalty.pointsUntilNextReward} ${this.loyalty.currency.pluralName} until your next reward.`
            : "You've unlocked every reward!";
          obj.availableRewardsText = `${this.rewardCount} Reward${this.rewardCount !== 1 ? 's' : ''} Available`;
          obj.hasImgLogo = false;

          if (this.loyalty.tier?.current?.name) {
            obj.headerText = this.loyalty.tier.current.name;
          }

          if (this.loyalty.isMembershipTierStatus) {
            obj.headerText = this.loyalty.tier.title;
            obj.descriptionText = this.loyalty.tier.description;
            obj.hasImgLogo = !!this.loyalty.tier.current?.badgeDefaultUrl;
            obj.imgSrc = this.loyalty.tier.current?.badgeDefaultUrl;
            obj.imageAlt = this.loyalty.tier.current.name;
          }
        }

        return obj;
      }
    },

    methods: {
      enrollInLoyalty() {
        events.emit('enroll-in-loyalty:open', {
          forceOpen: true,
          skipInitialContent: true
        });
      }
    }
  };
</script>
